import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface AddStudentFormProps {
  onSubmit: (firstname: string, lastname: string, email: string, isInvite: boolean) => void;
  onCancel: () => void;
}

const AddStudentForm: React.FC<AddStudentFormProps> = ({ onSubmit, onCancel }) => {
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [isInvite, setIsInvite] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstname.trim() && lastname.trim()) {
      setIsSubmitting(true);
      onSubmit(firstname.trim(), lastname.trim(), email.trim(), isInvite);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-grow mb-5">
        <div>
          <Label htmlFor="new-student-firstname">{t('students.firstName')}</Label>
          <Input
            id="new-student-firstname"
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            placeholder={t('students.firstNamePlaceholder')}
            autoFocus
          />
        </div>
        <div className="mt-4">
          <Label htmlFor="new-student-lastname">{t('students.lastName')}</Label>
          <Input
            id="new-student-lastname"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            placeholder={t('students.lastNamePlaceholder')}
          />
        </div>
        <div className="mt-4">
          <Label htmlFor="new-student-email">{t('students.email')} ({t('students.optional')})</Label>
          <Input
            id="new-student-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('students.emailPlaceholder')}
          />
        </div>
        <div className="mt-4 flex items-center space-x-2">
          <Checkbox
            id="is-invite"
            checked={isInvite}
            onCheckedChange={(checked) => setIsInvite(checked as boolean)}
            disabled={!email.trim()}
          />
          <Label htmlFor="is-invite">{t('students.sendInvite')}</Label>
        </div>
      </div>
      <div className={`flex gap-2 pt-4 mt-4 border-t ${
        isMobile
          ? 'fixed bottom-0 left-0 right-0 bg-gray-50 p-4 z-50 justify-center rounded-t-3xl border-gray-200 pb-7'
          : 'justify-end'
      }`}>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!firstname.trim() || !lastname.trim() || isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center">
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              {t('common.processing')}
            </span>
          ) : (
            t('common.done')
          )}
        </Button>
      </div>
    </form>
  );
};

export default AddStudentForm;
